import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Empty } from 'antd';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import { API_URLS } from '../config';
// Change SVG imports to use ReactComponent
import { ReactComponent as DirectoryLogo } from '../images/directory.svg';
import { ReactComponent as ArrowLogo } from '../images/arrow.svg';
import DateRangePicker from './components/DateRangePicker';
import Chart from './components/Chart';
import KeyMetrics from './components/KeyMetrics';
import SentimentAnalysis from './components/SentimentAnalysis';
import ChatBot from './components/ChatBot';
import RecentReviews from './components/RecentReviews';
import PieChart from './components/PieChart'; // Ensure PieChart is imported
import CategoryCarousel from './components/category/CategoryCarousel';
import FeedbackCarousel from './components/FeedbackCarousel';
import MiniStats from './components/MiniStats'; // Assuming MiniStats exists
import './styles/Home.css';

dayjs.extend(objectSupport);

const Home = () => {
  const directoryName = localStorage.getItem('businessName') || '__Business Name__';
  const navigate = useNavigate();
  const userName = localStorage.getItem('name') || 'User';
  const businessId = localStorage.getItem('businessId');

  // State for Date-Range dependent data
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState(null);
  const [chartKey, setChartKey] = useState(0);
  const [currentMaxReviews, setCurrentMaxReviews] = useState(1); // State for maxReviews
  const [periodData, setPeriodData] = useState({
    averageRating: '...', totalReviews: '...', positiveReviews: '...',
    negativeReviews: '...', busiestDay: '...', peakReviews: '...'
  });

  // Add state for Pie Chart data
  const [pieChartData, setPieChartData] = useState([]);

  // Add state for Category Carousel data
  const [categoryData, setCategoryData] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [categoryError, setCategoryError] = useState(null);

  // Add state for Feedbacks data
  const [feedbacksData, setFeedbacksData] = useState([]);
  const [feedbacksLoading, setFeedbacksLoading] = useState(true);
  const [feedbacksError, setFeedbacksError] = useState(null);

  // Add state for Top Themes data
  const [topThemesData, setTopThemesData] = useState({ positive: [], negative: [] });
  const [topThemesLoading, setTopThemesLoading] = useState(true);
  const [topThemesError, setTopThemesError] = useState(null);

  // State for MiniStats (Overview - not date-range dependent)
  const [miniStatsData, setMiniStatsData] = useState(null);
  const [miniStatsLoading, setMiniStatsLoading] = useState(true);
  const [miniStatsError, setMiniStatsError] = useState(null);

  // Function to calculate key metrics from raw time-series data
  const calculateKeyMetrics = (rawData) => {
    if (!rawData || rawData.length === 0) {
      return { averageRating: 'N/A', totalReviews: 0, positiveReviews: 0, negativeReviews: 0, busiestDay: 'N/A', peakReviews: 0 };
    }

    let totalRatingSum = 0;
    let totalReviewsCount = 0;
    let totalPositiveReviews = 0; // Calculated from 4 & 5 star counts
    let totalNegativeReviews = 0; // Calculated from 1 & 2 & 3 star counts
    let peakReviews = 0;
    let busiestDay = rawData[0].date;

    rawData.forEach(day => {
      const dailyReviews = parseInt(day.reviews, 10);
      const dailyRating = parseFloat(day.rating);
      // Sum positive (4+5) and negative (1+2) from daily counts
      totalPositiveReviews += (parseInt(day.rating_4_count, 10) || 0) + (parseInt(day.rating_5_count, 10) || 0);
      totalNegativeReviews += (parseInt(day.rating_1_count, 10) || 0) + (parseInt(day.rating_2_count, 10) || 0) + (parseInt(day.rating_3_count, 10) || 0);

      totalRatingSum += dailyRating * dailyReviews;
      totalReviewsCount += dailyReviews;

      if (dailyReviews > peakReviews) {
        peakReviews = dailyReviews;
        busiestDay = day.date;
      }
    });

    const averageRating = totalReviewsCount > 0 ? (totalRatingSum / totalReviewsCount).toFixed(1) : 'N/A';
    const formattedBusiestDay = busiestDay !== 'N/A' ? dayjs(busiestDay).format('MMM DD') : 'N/A';

    return {
      averageRating,
      totalReviews: totalReviewsCount,
      positiveReviews: totalPositiveReviews, // Use calculated sum
      negativeReviews: totalNegativeReviews, // Use calculated sum
      busiestDay: formattedBusiestDay,
      peakReviews
    };
  };

  // Function to calculate and format data for the Pie Chart
  const calculatePieChartData = (rawData) => {
    if (!rawData || rawData.length === 0) {
      return []; // Return empty array if no data
    }

    const totals = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    rawData.forEach(day => {
      totals[1] += parseInt(day.rating_1_count, 10) || 0;
      totals[2] += parseInt(day.rating_2_count, 10) || 0;
      totals[3] += parseInt(day.rating_3_count, 10) || 0;
      totals[4] += parseInt(day.rating_4_count, 10) || 0;
      totals[5] += parseInt(day.rating_5_count, 10) || 0;
    });

    // Define colors for each star rating
    const colors = {
      5: '#10b981', // Emerald 500
      4: '#8B4513', // Coffee brown
      3: '#f59e0b', // Amber 500
      2: '#f97316', // Orange 500
      1: '#ef4444'  // Red 500
    };

    // Format data for the PieChart component
    const formattedData = Object.entries(totals)
      .map(([rating, count]) => ({
        label: `${rating}-Star`,
        value: count,
        color: colors[rating]
      }))
      .sort((a, b) => parseInt(b.label) - parseInt(a.label)); // Sort 5-star down to 1-star

    return formattedData;
  };

  // Function to calculate needed 5-star reviews for the next 0.5 step
  const calculateNeededFiveStars = (averageRating, totalReviews, ratingSum) => {
    if (averageRating === null || totalReviews === null || ratingSum === null || averageRating >= 5) {
      return 0; // Already at max or data missing
    }
    // Ensure inputs are numbers
    const avg = Number(averageRating);
    const total = Number(totalReviews);
    const sum = Number(ratingSum);

    if (isNaN(avg) || isNaN(total) || isNaN(sum)) {
        return 'N/A'; // Invalid input
    }

    const targetAvg = Math.ceil(avg * 2) / 2;
    if (targetAvg >= 5) { // If target is 5 or more, calculate for 5
        // If avg is already 5, needed is 0. If avg < 5, target is 5.
        // Recalculate: (sum + 5x) / (total + x) = 5 => sum + 5x = 5*total + 5x => sum = 5*total
        // This formula isn't quite right for target 5. Let's use the general one.
        if (5 - targetAvg <= 0) return 0; // Avoid division by zero/negative if target is 5 or higher
        const neededForFive = (targetAvg * total - sum) / (5 - targetAvg);
         return neededForFive <= 0 ? 0 : Math.ceil(neededForFive); // Return 0 if already met or exceeded
    }


    const needed = (targetAvg * total - sum) / (5 - targetAvg);

    // Return 0 if the target is already met or exceeded, otherwise ceil the result
    return needed <= 0 ? 0 : Math.ceil(needed);
  };


  // Fetch data when dateRange or businessId changes
  useEffect(() => {
    if (!businessId) {
      setChartError('Business ID not found. Please log in again.');
      setCategoryError('Business ID not found. Please log in again.');
      setFeedbacksError('Business ID not found. Please log in again.'); // Also set feedback error
      setTopThemesError('Business ID not found. Please log in again.'); // Set top themes error
      setChartLoading(false);
      setCategoryLoading(false);
      setFeedbacksLoading(false); // Also set feedback loading
      setTopThemesLoading(false); // Set top themes loading
      return;
    }
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
      console.warn('Date range not fully specified, using default or skipping fetch.');
      setChartLoading(false);
      setCategoryLoading(false);
      setFeedbacksLoading(false);
      setTopThemesLoading(false); // Set top themes loading
      return;
    }

    const fetchData = async () => {
      setChartLoading(true);
      setCategoryLoading(true);
      setFeedbacksLoading(true); // Set feedback loading true
      setTopThemesLoading(true); // Set top themes loading true
      setChartError(null);
      setCategoryError(null);
      setFeedbacksError(null); // Reset feedback error
      setTopThemesError(null); // Reset top themes error

      // Reset data states before fetching
      setChartData([]);
      setPieChartData([]);
      setPeriodData({ averageRating: '...', totalReviews: '...', positiveReviews: '...', negativeReviews: '...', busiestDay: '...', peakReviews: '...' });
      setCategoryData([]);
      setFeedbacksData([]);
      setTopThemesData({ positive: [], negative: [] });

      try {
        const startDate = dayjs(dateRange.startDate).format('YYYY-MM-DD');
        const endDate = dayjs(dateRange.endDate).format('YYYY-MM-DD');
        const limitThemes = 5; // Define how many themes per sentiment to fetch

        const commonHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };

        // Prepare URLs
        const timeSeriesUrl = `${API_URLS.timeSeries(businessId)}?startDate=${startDate}&endDate=${endDate}`;
        const topicsUrl = `${API_URLS.topics(businessId)}?startDate=${startDate}&endDate=${endDate}`;
        const feedbackUrl = `${API_URLS.textFeedback(businessId)}?limit=50&offset=0&startDate=${startDate}&endDate=${endDate}`;
        const topThemesUrl = `${API_URLS.topThemes(businessId)}?startDate=${startDate}&endDate=${endDate}&limit=${limitThemes}`;

        const fetchDataWithErrorHandling = async (url, dataType) => {
          if (dataType === 'timeSeries') {
            console.log('Attempting to fetch timeSeries data from:', url);
          }
          try {
            const response = await axios.get(url, { headers: commonHeaders });
            if (dataType === 'timeSeries') {
              console.log('Successfully fetched timeSeries data. Status:', response.status);
              console.log('TimeSeries API Response Data:', JSON.stringify(response.data, null, 2));
            }
            return response.data; // Return data on success
          } catch (err) {
            // --- Start of new diagnostic logs ---
            console.log(`[Catch Block Debug] Error caught for URL: '${url}', DataType: '${dataType}'`);
            if (err.response) {
              console.log(`[Catch Block Debug] Error status: ${err.response.status}`);
            } else {
              console.log(`[Catch Block Debug] No err.response object found.`);
            }
            // --- End of new diagnostic logs ---

            // Specific 404 handling for time-series URLs, now with dataType check.
            if (err.response && err.response.status === 404 && typeof url === 'string' && url.includes('/time-series') && dataType === 'timeSeries') {
              console.log(`[Debug] Caught 404 for time-series URL ('${url}') with dataType 'timeSeries'. Treating as empty data. Returning empty array.`);
              return []; 
            }

            // Existing logging for other time-series errors (non-404 or if not matching the above 404 condition)
            if (dataType === 'timeSeries') {
              console.log('Error fetching timeSeries data (non-404 or other issue). Error message:', err.message);
              if (err.response) {
                // This logging is somewhat redundant if the 404 case above is hit, but fine for other errors.
                console.log('Error response status (timeSeries):', err.response.status);
                console.log('Error response data (timeSeries):', JSON.stringify(err.response.data, null, 2));
              }
            }
            
            // Generic error logging and return for all other cases not handled above
            console.error(`Error fetching ${dataType} data (generic path):`, err);
            return { error: true, type: dataType, message: err.response?.data?.error || `Failed to load ${dataType} data.` };
          }
        };

        const results = await Promise.all([
          fetchDataWithErrorHandling(timeSeriesUrl, 'timeSeries'),
          fetchDataWithErrorHandling(topicsUrl, 'topics'),
          fetchDataWithErrorHandling(feedbackUrl, 'feedbacks'),
          fetchDataWithErrorHandling(topThemesUrl, 'topThemes')
        ]);

        const timeSeriesResult = results[0];
        const topicsResult = results[1];
        const feedbackResult = results[2];
        const topThemesResult = results[3];

        console.log('Processing timeSeriesResult:', JSON.stringify(timeSeriesResult, null, 2));

        // --- Process Time Series Data (Chart and PieChart) ---
        if (timeSeriesResult.error) {
          console.log('Condition for setting chartError met: timeSeriesResult.error is true. Message:', timeSeriesResult.message);
          setChartError(timeSeriesResult.message);
          setChartData([]);
          setPieChartData([]); // Ensure pie chart data is also empty on timeSeries error
          setPeriodData({ averageRating: 'N/A', totalReviews: 'N/A', positiveReviews: 'N/A', negativeReviews: 'N/A', busiestDay: 'N/A', peakReviews: 'N/A' });
        } else {
          const rawTimeSeriesData = timeSeriesResult;
          if (Array.isArray(rawTimeSeriesData) && rawTimeSeriesData.length === 0) {
            // Updated log to be more specific about PieChart impact
            console.log('[PieChart Debug] TimeSeries data (source for PieChart) is an empty array. PieChart will be empty (pieChartData will be []). chartError remains null.');
          }
          
          console.log('[PieChart Debug] Calculating PieChart data using rawTimeSeriesData derived from timeSeriesResult. rawTimeSeriesData:', JSON.stringify(rawTimeSeriesData, null, 2));
          const calculatedMetrics = calculateKeyMetrics(rawTimeSeriesData);
          setPeriodData(calculatedMetrics);
          const calculatedPieData = calculatePieChartData(rawTimeSeriesData);
          console.log('[PieChart Debug] Result from calculatePieChartData. Setting pieChartData to:', JSON.stringify(calculatedPieData, null, 2));
          setPieChartData(calculatedPieData);
          
          // Find the maximum value of reviews in the raw data
          let calculatedMaxReviews = rawTimeSeriesData.reduce((max, item) => Math.max(max, parseFloat(item.reviews) || 0), 0);
        calculatedMaxReviews = calculatedMaxReviews > 0 ? calculatedMaxReviews : 1; // Ensure maxReviews is at least 1
        setCurrentMaxReviews(calculatedMaxReviews); // Update state

        const transformedChartData = [
          ...rawTimeSeriesData.map(item => ({
            date: item.date,
            value: parseFloat((parseFloat(item.rating)).toFixed(1)), // Keep rating as 0-5, ensure it's a number
            category: 'Average Rating'
          })),
          ...rawTimeSeriesData.map(item => ({
            date: item.date,
            // Scale review count to 0-5 range, ensure it's a number
            value: parseFloat(((parseFloat(item.reviews) / calculatedMaxReviews * 5)).toFixed(1)), 
            category: 'Review Count'
          }))
        ];
          console.log('Setting chartData with (transformed from timeSeriesResult):', JSON.stringify(transformedChartData, null, 2));
          setChartData(transformedChartData);
          setChartKey(prevKey => prevKey + 1);
        }

        // --- Process Topics Data (CategoryCarousel) ---
        if (topicsResult.error) {
          setCategoryError(topicsResult.message);
          setCategoryData([]);
        } else {
          const formattedCategoryData = topicsResult.map(topic => ({
              name: topic.topic_name,
              rating: parseFloat(topic.rating).toFixed(1),
              totalResponses: topic.total_responses
          }));
          setCategoryData(formattedCategoryData);
        }

        // --- Process Feedback Data (RecentReviews, FeedbackCarousel) ---
        if (feedbackResult.error) {
          setFeedbacksError(feedbackResult.message);
          setFeedbacksData([]);
        } else {
          const formattedFeedbacksData = feedbackResult.map(fb => ({
              id: fb.feedback_id,
              submission_id: fb.submission_id,
              text: fb.feedback_text,
              rating: fb.rating_value,
              date: dayjs(fb.submission_date).format('YYYY-MM-DD'),
              // Only include usefulness data if it exists and is not the default
              usefulnessScore: (fb.usefulness_score && fb.usefulness_score > 0) ? fb.usefulness_score : null,
              usefulnessLabel: (fb.usefulness_label && fb.usefulness_label !== 'Not Useful') ? fb.usefulness_label : null,
              // Only include sentiment data if it exists and is valid
              sentimentLabel: (fb.sentiment_label && fb.sentiment_label !== 'neutral') ? fb.sentiment_label : null,
              sentimentScore: (fb.sentiment_score && fb.sentiment_score > 0) ? fb.sentiment_score : null
          }));
          setFeedbacksData(formattedFeedbacksData);
        }

        // --- Process Top Themes Data (SentimentAnalysis) ---
        if (topThemesResult.error) {
          setTopThemesError(topThemesResult.message);
          setTopThemesData({ positive: [], negative: [] });
        } else {
          // Ensure topThemesResult is an object with positive and negative keys,
          // defaulting to empty arrays if not present (though fetchDataWithErrorHandling should give object from API)
          setTopThemesData({
            positive: topThemesResult?.positive || [],
            negative: topThemesResult?.negative || []
          });
        }

      } catch (err) {
        // This catch block is now for unexpected errors during Promise.all itself or subsequent processing
        // Individual fetch errors are handled above.
        console.error("Unexpected error in fetchData processing:", err);
        const genericErrorMsg = "An unexpected error occurred while processing dashboard data.";
        setChartError(chartError || genericErrorMsg); // Preserve specific errors if they were set
        setCategoryError(categoryError || genericErrorMsg);
        setFeedbacksError(feedbacksError || genericErrorMsg);
        setTopThemesError(topThemesError || genericErrorMsg);
      } finally {
        setChartLoading(false);
        setCategoryLoading(false);
        setFeedbacksLoading(false);
        setTopThemesLoading(false);
      }
    };

    fetchData();
  }, [dateRange, businessId]);


  // Fetch Overview data for MiniStats (independent of date range)
  useEffect(() => {
    if (!businessId) {
      setMiniStatsError('Business ID not found. Please log in again.');
      setMiniStatsLoading(false);
      return;
    }

    const fetchOverviewData = async () => {
      setMiniStatsLoading(true);
      setMiniStatsError(null);
      try {
        const overviewUrl = API_URLS.overview(businessId);
        const response = await axios.get(overviewUrl, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        const overviewData = response.data;

        // Calculate the needed 5-star reviews
        const neededStars = calculateNeededFiveStars(
          overviewData.average_rating,
          overviewData.total_feedbacks, // Use total_feedbacks from backend
          overviewData.rating_sum       // Use rating_sum from backend
        );

        // Add the calculated value to the data object
        setMiniStatsData({
          ...overviewData,
          needed_five_stars: neededStars
        });

      } catch (err) {
        console.error("Error fetching overview data:", err);
        setMiniStatsError(err.response?.data?.error || 'Failed to load overview stats.');
        setMiniStatsData(null); // Clear data on error
      } finally {
        setMiniStatsLoading(false);
      }
    };

    fetchOverviewData();
  }, [businessId]); // Re-run only when businessId changes


  // Handle date range changes from the picker
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  // Effect for glass box mouse move (remains the same)
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';

    // Find all glass-box elements after the component mounts
    const glassBoxes = document.querySelectorAll('.glass-box');

    // Define the event handler functions
    const handleMouseMove = (e) => {
      const element = e.currentTarget; // Use currentTarget to get the element the listener is attached to
      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      element.style.setProperty('--mouse-x', `${x}px`);
      element.style.setProperty('--mouse-y', `${y}px`);
    };

    const handleMouseLeave = (e) => {
      const element = e.currentTarget;
      element.style.removeProperty('--mouse-x');
      element.style.removeProperty('--mouse-y');
    };

    // Add event listeners to each element
    glassBoxes.forEach(element => {
      element.addEventListener('mousemove', handleMouseMove);
      element.addEventListener('mouseleave', handleMouseLeave);
    });

    // Cleanup function: Remove event listeners when the component unmounts
    return () => {
      glassBoxes.forEach(element => {
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, []);

  return (
    <div className="dashboard-content">
      {/* Header */}
      <div className='dashboard-header'>
        <div className='directory-info'>
          <div className="directory-logo">
            <div className='directory-logo'>
              <DirectoryLogo />
            </div>
            <p>{directoryName}</p>
          </div>
        </div>
        <h1>Hello, {userName}</h1>
      </div>

      {/* Main dashboard grid layout */}
      <div className='dashboard-grid'>
        {/* MiniStats */}
        <div className="mini-stats-area ">
          {/* Pass overview data, loading, and error state to MiniStats */}
          <MiniStats
            loading={miniStatsLoading}
            error={miniStatsError}
            data={miniStatsData}
          />
        </div>

        {/* Left Column */}
        <div className="left-column">
          {/* Chart Area */}
          <div className="main-chart-container glass-box">
            <div className="chart-with-datepicker">
              <DateRangePicker onChange={handleDateRangeChange} />
              {/* Pass chart data, loading, error, and key to Chart */}
              <Chart
                chartData={chartData}
                loading={chartLoading}
                error={chartError}
                maxReviews={currentMaxReviews} // Pass currentMaxReviews to Chart component
                categoryData={categoryData} // Pass category data for trends toggle
                key={chartKey} // Use key to force remount on data change
              />
            </div>
          </div>

          {/* Key Metrics Area */}
          <div className="metrics-breakdown-container glass-box">
            {/* Pass calculated periodData */}
            <KeyMetrics periodData={periodData} />
          </div>

          {/* Sentiment Analysis */}
          <div className="sentiment-analysis-container glass-box">
            {/* Pass fetched topThemesData, loading, and error states */}
            <SentimentAnalysis
              loading={topThemesLoading}
              error={topThemesError}
              data={topThemesData}
            />
          </div>

          {/* ChatBot area */}
          <div className="chatbot-area chatbot-container">
            <ChatBot />
          </div>
        </div>

        {/* Right Column */}
        <div className="right-column">
          {/* Recent Reviews */}
          <div className="recent-reviews-container glass-box">
            {/* Pass fetched feedbacksData, handle loading/error */}
            {feedbacksLoading && <div className="loading-message">Loading reviews...</div>}
            {!feedbacksLoading && feedbacksError && <div className="error-message">{feedbacksError}</div>}
            {!feedbacksLoading && !feedbacksError && feedbacksData.length > 0 && <RecentReviews reviews={feedbacksData} />}
            {!feedbacksLoading && !feedbacksError && feedbacksData.length === 0 && <Empty description="No recent reviews yet. They will appear here once you receive feedback." />}
          </div>

          {/* Pie Chart */}
          <div className="pie-chart-wrapper glass-box pie-chart-container">
             {/* Pass the calculated pieChartData state */}
             {chartLoading && <div className="loading-message">Loading distribution...</div>}
             {!chartLoading && chartError && <div className="error-message">{chartError}</div>}
             {!chartLoading && !chartError && pieChartData.length > 0 && <PieChart data={pieChartData} />}
             {!chartLoading && !chartError && pieChartData.length === 0 && <Empty description="No review distribution data yet. This chart will populate as you get reviews." />}
          </div>

          {/* Category Carousel */}
          <div className="category-carousel-container">
            {/* Pass fetched categoryData, handle loading/error */}
            {categoryLoading && <div className="loading-message">Loading categories...</div>}
            {!categoryLoading && categoryError && <div className="error-message">{categoryError}</div>}
            {!categoryLoading && !categoryError && categoryData.length > 0 && <CategoryCarousel categories={categoryData} />}
            {!categoryLoading && !categoryError && categoryData.length === 0 && <Empty description="No category performance data for this period. Explore other date ranges or wait for more feedback." />}
          </div>

        </div>
      </div>
      {/* Feedback Carousel */}
      <div className="" style={{ marginTop: '1.5rem' }}>
         {/* Pass fetched feedbacksData, handle loading/error */}
         {feedbacksLoading && <div className="loading-message">Loading feedback carousel...</div>}
         {!feedbacksLoading && feedbacksError && <div className="error-message">{feedbacksError}</div>}
         {!feedbacksLoading && !feedbacksError && feedbacksData.length > 0 && <FeedbackCarousel feedbacks={feedbacksData} />}
         {!feedbacksLoading && !feedbacksError && feedbacksData.length === 0 && <Empty description="No feedback available to display in the carousel." />}
      </div>
    </div>
  );
};

export default Home;